/**
 * Zahlungsvorlagen - loest `PAYMENT_TEMPLATES` (config/payments.ts) als Datenquelle ab (Spec
 * 2026-09-01, Abschnitt 3). GET ist offen fuer alle Rollen (Constraint 9); POST, PATCH (seit der
 * Nutzerentscheidung vom 01.10.2026, Spec 2026-09-17 "Vorlage und Rate") und DELETE verlangen
 * manage_suppliers (backend/src/http/endpunktMatrix.ts).
 *
 * Der Name einer Vorlage wird nicht eingegeben: "[comment] - [Titel aus den Raten]"
 * (`lib/zahlungsvorlagenTitel.ts#vorlagenName`). `label` kommt vom Server als dieselbe abgeleitete
 * Zeichenkette; die Oberflaeche bildet den Namen selbst aus `comment` und `rows`, damit er auch bei
 * einem veralteten Snapshot zu den angezeigten Raten passt.
 */
import { anfrage } from "./basis.ts";
import type { PaymentTemplatePayment } from "../config/payments.ts";

/** Ein Lieferant, der die Vorlage freigegeben hat (nur im GET, fuer "Verwendet bei"). */
export interface VorlagenLieferant {
  kLieferant: number;
  /** `null`, wenn der Lieferant noch keine `supplier_extras`-Zeile (und damit keinen Kurznamen) hat. */
  shortName: string | null;
  /**
   * JTL-Firmenname (`tlieferant.cFirma`), vom Backend in EINEM Rundlauf fuer alle Lieferanten gelesen.
   * `null`, wenn JTL den Lieferanten nicht kennt oder nicht konfiguriert/erreichbar ist - die
   * Anzeige faellt dann auf `shortName` zurueck.
   */
  firma: string | null;
  isDefault: boolean;
}

export interface PaymentTemplateRow {
  id: string;
  /** Abgeleiteter Anzeigename (Server); die Anzeige nutzt `vorlagenName(comment, rows)`. */
  label: string;
  /** Freier, optionaler Kommentar - der einzige frei pflegbare Namensteil. */
  comment?: string | null;
  rows: PaymentTemplatePayment[];
  /** Fuer PATCH; fehlt nur bei dem eingebauten Rueckfall (`PAYMENT_TEMPLATES`). */
  rowVersion?: number;
  /** Nur im GET: freigebende Lieferanten. */
  suppliers?: VorlagenLieferant[];
  /** Nur im GET: Anzahl Bestellungen, die die Vorlage uebernommen haben (sie behalten ihren Plan). */
  orderCount?: number;
}

export function ladeVorlagen(): Promise<PaymentTemplateRow[]> {
  return anfrage<PaymentTemplateRow[]>("/api/payment-templates", { methode: "GET" });
}

export function vorlageAnlegen(
  comment: string | null,
  rows: PaymentTemplatePayment[],
): Promise<PaymentTemplateRow> {
  return anfrage<PaymentTemplateRow>("/api/payment-templates", {
    methode: "POST",
    koerper: { comment, rows },
  });
}

/**
 * Bearbeitet Kommentar und die VOLLSTAENDIGE Ratenliste einer Vorlage. 404, wenn die id nicht
 * existiert; 409 (`KonfliktFehler`, `aktuellerStand` = die gespeicherte Vorlage), wenn sie
 * zwischenzeitlich von jemand anderem geaendert wurde. Wirkt nur auf kuenftige Uebernahmen -
 * bestehende Bestellungen behalten ihren Plan.
 */
export function vorlageAendern(
  id: string,
  comment: string | null,
  rows: PaymentTemplatePayment[],
  rowVersion: number,
): Promise<PaymentTemplateRow> {
  return anfrage<PaymentTemplateRow>(`/api/payment-templates/${encodeURIComponent(id)}`, {
    methode: "PATCH",
    koerper: { comment, rows, rowVersion },
  });
}

/**
 * 404, wenn die id nicht existiert; 409 (`ApiFehler`/`KonfliktFehler`, siehe `api/basis.ts`), solange
 * mindestens ein Lieferant die Vorlage noch freigegeben hat - `error` traegt dann den Wortlaut aus
 * `backend/src/domaene/zahlungsvorlagen.ts` (ZahlungsvorlageInBenutzungError), der die betroffenen
 * Lieferanten nennt. Bestellungen blockieren nicht (sie tragen ihre eigene Kopie der Raten). Kein
 * `rowVersion` im Aufruf: ob geloescht werden darf, haengt nicht vom Bearbeitungsstand ab.
 */
export function vorlageLoeschen(id: string): Promise<{ ok: true }> {
  return anfrage<{ ok: true }>(`/api/payment-templates/${encodeURIComponent(id)}`, {
    methode: "DELETE",
  });
}
